import {
  ActivatedRouteSnapshot,
  CanActivateChildFn,
  CanActivateFn,
  Router,
  RouterStateSnapshot,
  Routes,
  RouterModule,
} from '@angular/router';
import { AuthCallback } from './auth/auth-callback.component';
import { ShellComponent } from './components/shell/shell.component';
import { CreateWorkOrderComponent } from './views/create-work-order/create-work-order.component';
import { AuthHelper } from './auth/auth-helper';
import { ForbiddenComponent } from './views/forbidden/forbidden.component';
import { AuthPolicies } from './auth/auth-policies';
import { inject, NgModule } from '@angular/core';
import { ViewWorkOrderComponent } from './views/view-work-order/view-work-order.component';
import { MasterDataConfigComponent } from './views/master-data-config/master-data-config.component';
import { FlagOnOffConfigComponent } from './views/flag-on-off-config/flag-on-off-config.component';
import { BulkWorkOrderComponent } from './views/bulk-work-order/bulk-work-order.component';
import { VscListViewComponent } from './views/vsc-list-view/vsc-list-view.component';
import { RetriggerWorkOrderComponent } from './views/retrigger-work-order/retrigger-work-order.component';
import { ShopComponent } from './views/shop/shop.component';
import { ListAllProcessedWorkOrdersComponent } from './views/list-all-processed-work-orders/list-all-processed-work-orders.component';
import { ListWorkOrdersComponent } from './views/list-work-orders/list-work-orders.component';
import { UserComponent } from './views/user/user.component';
import { ValidationErrorsComponent } from './views/validation-errors/validation-errors.component';
import { RaAlertsComponent } from './views/ra-alerts/ra-alerts.component';
import { ListMyProcessedWorkOrdersComponent } from './views/list-my-processed-work-orders/list-my-processed-work-orders.component';
import { ConatainerMrlComponent } from './views/conatainer-mrl/conatainer-mrl.component';
import { ChatbotComponent } from './views/chatbot/chatbot.component';
import { MasterDataComponent } from './views/master-data/master-data.component';
import { ShopManagementComponent } from './views/shop-management/shop-management.component';
import { ShopContractComponent } from './views/shop-contract/shop-contract.component';
import { LabourRatesComponent } from './views/labour-rates/labour-rates.component';
import { MaterialRateComponent } from './views/material-rate/material-rate.component';
import { OemDiscountComponent } from './views/oem-discount/oem-discount.component';
import { PartsManagementComponent } from './views/parts-management/parts-management.component';
import { CreatePartComponent } from './views/create-part/create-part.component';
import { ViewPartsManagementComponent } from './views/view-parts-management/view-parts-management.component';
import { CreateShopsComponent } from './views/create-shops/create-shops.component';
import { environment } from '../environments/environment';
import { DamageCodeComponent } from './views/damage-code/damage-code.component';
import { CreateDamageCodeComponent } from './views/create-damage-code/create-damage-code.component';
import { RepairPartComponent } from './views/repair-part/repair-part.component';
import { CreateRepairPartComponent } from './views/create-repair-part/create-repair-part.component';
import { RepairCodeListComponent } from './views/repair-code/repair-code.component';
import { CreateRepairCodeComponent } from './views/create-repair-code/create-repair-code.component';
import { RepairCodeExclusionListComponent } from './views/repair-code-exclusion/repair-code-exclusion.component';
import { CreateRepairCodeExclusionComponent } from './views/create-repair-code-exclusion/create-repair-code-exclusion.component';
import { PartExclusionComponent } from './views/part-exclusion/part-exclusion.component';
import { PartExclusionFormComponent } from './views/part-exclusion-form/part-exclusion-form.component';
import { UserDetailsComponent } from './components/user-details/user-details.component';
import { CreateUserComponent } from './components/user/create-user/create-user.component';
import { ContainerGradeRepairCodeListComponent } from './views/container-grade-repair-code/container-grade-repair-code.component';
import { CreateContainerGradeRepairCodeComponent } from './views/create-container-grade-repair-code/create-container-grade-repair-code.component';
import { RepairLocationListComponent } from './views/repair-location/repair-location.component';
import { CreateRepairLocationComponent } from './views/create-repair-location/create-repair-location.component';
import { RepairModeListComponent } from './views/repair-mode/repair-mode.component';
import { CreateRepairModeComponent } from './views/create-repair-mode/create-repair-mode.component';
import { MasterDataService } from './services/master-data.service';
import { firstValueFrom } from 'rxjs';
import { InAdvanceContainerVisibilityComponent } from './components/in-advance-container-visibility/in-advance-container-visibility.component';
import { BookingAssignmentComponent } from './components/booking-assignment/booking-assignment.component';
import { VscSettingsComponent } from './views/vsc-settings/vsc-settings.component';
import { VscDetailedViewComponent } from './views/vsc-detailed-view/vsc-detailed-view.component';
import { VesselWoComponent } from './views/vessel-wo/vessel-wo.component';

const userAuthenticatedRouteGuard: CanActivateFn & CanActivateChildFn = async (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot,
) => {
  if (environment.DEV_BEARER_TOKEN) {
    if (!AuthHelper.user) {
      const masterDataService = inject(MasterDataService);
      try {
        const user = await firstValueFrom(masterDataService.getUser());
        user.maintenanceShops.sort((a, b) => a.maintenanceShopCode.localeCompare(b.maintenanceShopCode));
        AuthHelper.user = user;
      } catch {
        // User fetch failed — app will proceed with no roles
      }
    }
    return true;
  }
  if (!AuthHelper.isAccessTokenPresentAndValid()) {
    await AuthHelper.refreshAccessToken(state.url);
  }
  return AuthHelper.isAccessTokenPresentAndValid();
};

const isUserShopOrAdminRouteGuard: CanActivateFn = (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
  let authPolicies: AuthPolicies = inject(AuthPolicies);
  if (authPolicies.canCreateWorkOrder) {
    return true;
  }
  globalThis.location.href = '/forbidden';
  return false;
};

const isVesselWoFileProcessingRouteGuard: CanActivateFn = (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
  let authPolicies: AuthPolicies = inject(AuthPolicies);
  if (authPolicies.canViewVesselWoProcessing && environment.Features.EnableVesselWoFileProcessing) {
    return true;
  }
  globalThis.location.href = '/forbidden';
  return false;
};

const isUserAppSupportAdminRouteGuard: CanActivateFn & CanActivateChildFn = (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
  let authPolicies: AuthPolicies = inject(AuthPolicies);
  if (authPolicies.isAppSupportAdmin) {
    return true;
  }
  globalThis.location.href = '/forbidden';
  return false;
};
const isRetriggerRouteGuard: CanActivateFn = (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
  let authPolicies: AuthPolicies = inject(AuthPolicies);
  if (authPolicies.canAccessRetriggerPage) {
    return true;
  }
  globalThis.location.href = '/forbidden';
  return false;
};
const isUserGSCOrAdminOrAppSupportAdminRouteGuard: CanActivateFn = (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
  let authPolicies: AuthPolicies = inject(AuthPolicies);
  if (authPolicies.isUserGSCorAdminorAppSupportAdminAccess) {
    return true;
  }
  globalThis.location.href = '/forbidden';
  return false;
};

const canViewMyWorkOrdersRouteGuard: CanActivateFn = (_route: ActivatedRouteSnapshot, _state: RouterStateSnapshot) => {
  const authPolicies: AuthPolicies = inject(AuthPolicies);
  if (authPolicies.canViewMyWorkOrder) {
    return true;
  }
  globalThis.location.href = '/forbidden';
  return false;
};

const canViewApprovalSettingsRouteGuard: CanActivateFn = (_route: ActivatedRouteSnapshot, _state: RouterStateSnapshot) => {
  const authPolicies: AuthPolicies = inject(AuthPolicies);
  if (authPolicies.canViewApprovalSettings) {
    return true;
  }
  globalThis.location.href = '/forbidden';
  return false;
};

const isEmailIdAllowedRouteGuard: CanActivateFn & CanActivateChildFn = (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
  let authPolicies: AuthPolicies = inject(AuthPolicies);
  if (authPolicies.isEmailIdAllowed) {
    return true;
  }
  globalThis.location.href = '/forbidden';
  return false;
};

const isRiseImageAnalyticsRouteGuard: CanActivateFn & CanActivateChildFn = (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
  if (environment.Features.EnableRiseImageAnalytics) {
    return true;
  }
  globalThis.location.href = '/forbidden';
  return false;
};

const isMasterDataRouteGuard: CanActivateFn & CanActivateChildFn = (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
  let authPolicies: AuthPolicies = inject(AuthPolicies);
  if (authPolicies.canViewMasterData && environment.Features.EnableShopManagementAddEdit) {
    return true;
  }
  globalThis.location.href = '/forbidden';
  return false;
};

const isShopManagementViewRouteGuard: CanActivateFn = (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
  let authPolicies: AuthPolicies = inject(AuthPolicies);
  if (authPolicies.canViewShopManagement && environment.Features.EnableShopManagementAddEdit) {
    return true;
  }
  globalThis.location.href = '/forbidden';
  return false;
};

const isShopManagementAddRouteGuard: CanActivateFn = (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
  let authPolicies: AuthPolicies = inject(AuthPolicies);
  if (authPolicies.canAddShopManagement && environment.Features.EnableShopManagementAddEdit) {
    return true;
  }
  globalThis.location.href = '/forbidden';
  return false;
};

const isShopManagementEditRouteGuard: CanActivateFn = (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
  let authPolicies: AuthPolicies = inject(AuthPolicies);
  if (authPolicies.canEditShopManagement && environment.Features.EnableShopManagementAddEdit) {
    return true;
  }
  globalThis.location.href = '/forbidden';
  return false;
};

const isPartManagementViewRouteGuard: CanActivateFn = (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
  let authPolicies: AuthPolicies = inject(AuthPolicies);
  if (authPolicies.canViewPartManagement && environment.Features.EnablePartsManagement) {
    return true;
  }
  globalThis.location.href = '/forbidden';
  return false;
};

const isPartManagementAddRouteGuard: CanActivateFn = (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
  let authPolicies: AuthPolicies = inject(AuthPolicies);
  if (authPolicies.canAddPartManagement && environment.Features.EnablePartsManagement) {
    return true;
  }
  globalThis.location.href = '/forbidden';
  return false;
};

const isPartManagementEditRouteGuard: CanActivateFn = (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
  let authPolicies: AuthPolicies = inject(AuthPolicies);
  if (authPolicies.canEditPartManagement && environment.Features.EnablePartsManagement) {
    return true;
  }
  globalThis.location.href = '/forbidden';
  return false;
};

const isShopContractViewRouteGuard: CanActivateFn & CanActivateChildFn = (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
  let authPolicies: AuthPolicies = inject(AuthPolicies);
  if (authPolicies.canViewShopContract && environment.Features.EnableContractRatesAndDiscount) {
    return true;
  }
  globalThis.location.href = '/forbidden';
  return false;
};

const isOemDiscountViewRouteGuard: CanActivateFn = (_route: ActivatedRouteSnapshot, _state: RouterStateSnapshot) => {
  let authPolicies: AuthPolicies = inject(AuthPolicies);
  if (authPolicies.canViewOemDiscount && environment.Features.EnableContractRatesAndDiscount) {
    return true;
  }
  globalThis.location.href = '/forbidden';
  return false;
};

const isOemDiscountAddRouteGuard: CanActivateFn = (_route: ActivatedRouteSnapshot, _state: RouterStateSnapshot) => {
  let authPolicies: AuthPolicies = inject(AuthPolicies);
  if (
    authPolicies.canAddOemDiscount &&
    environment.Features.EnableContractRatesAndDiscount &&
    environment.Features.EnableOemDiscountActions
  ) {
    return true;
  }
  globalThis.location.href = '/forbidden';
  return false;
};

const isOemDiscountEditRouteGuard: CanActivateFn = (_route: ActivatedRouteSnapshot, _state: RouterStateSnapshot) => {
  let authPolicies: AuthPolicies = inject(AuthPolicies);
  if (
    authPolicies.canEditOemDiscount &&
    environment.Features.EnableContractRatesAndDiscount &&
    environment.Features.EnableOemDiscountActions
  ) {
    return true;
  }
  globalThis.location.href = '/forbidden';
  return false;
};

const isDamageCodeViewRouteGuard: CanActivateFn = (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
  let authPolicies: AuthPolicies = inject(AuthPolicies);
  if (authPolicies.canViewDamageCode && environment.Features.EnableDamageCode) {
    return true;
  }
  globalThis.location.href = '/forbidden';
  return false;
};

const isDamageCodeAddRouteGuard: CanActivateFn = (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
  let authPolicies: AuthPolicies = inject(AuthPolicies);
  if (authPolicies.canAddDamageCode && environment.Features.EnableDamageCode) {
    return true;
  }
  globalThis.location.href = '/forbidden';
  return false;
};

const isDamageCodeEditRouteGuard: CanActivateFn = (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
  let authPolicies: AuthPolicies = inject(AuthPolicies);
  if (authPolicies.canEditDamageCode && environment.Features.EnableDamageCode) {
    return true;
  }
  globalThis.location.href = '/forbidden';
  return false;
};

const isRepairPartViewRouteGuard: CanActivateFn = (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
  let authPolicies: AuthPolicies = inject(AuthPolicies);
  if (authPolicies.canViewRepairPart && environment.Features.EnableRepairPart) {
    return true;
  }
  globalThis.location.href = '/forbidden';
  return false;
};

const isRepairPartAddRouteGuard: CanActivateFn = (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
  let authPolicies: AuthPolicies = inject(AuthPolicies);
  if (authPolicies.canAddRepairPart && environment.Features.EnableRepairPart) {
    return true;
  }
  globalThis.location.href = '/forbidden';
  return false;
};

const isRepairPartEditRouteGuard: CanActivateFn = (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
  let authPolicies: AuthPolicies = inject(AuthPolicies);
  if (authPolicies.canEditRepairPart && environment.Features.EnableRepairPart) {
    return true;
  }
  globalThis.location.href = '/forbidden';
  return false;
};

const isContainerVisibilityRouteGuard: CanActivateFn = (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
  let authPolicies: AuthPolicies = inject(AuthPolicies);
  if (authPolicies.canViewContainerVisibility) {
    return true;
  }
  globalThis.location.href = '/forbidden';
  return false;
};

const canViewBookingAssignmentRouteGuard: CanActivateFn = (_route: ActivatedRouteSnapshot, _state: RouterStateSnapshot) => {
  const authPolicies: AuthPolicies = inject(AuthPolicies);
  if (authPolicies.canViewBookingAssignment && environment.Features.EnableBookingAssignment) {
    return true;
  }
  globalThis.location.href = '/forbidden';
  return false;
};

const isContainerStockRouteGuard: CanActivateFn = (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
  let authPolicies: AuthPolicies = inject(AuthPolicies);
  if (environment.Features.EnableContainerStockView && authPolicies.canViewContainerStock) {
    return true;
  }
  globalThis.location.href = '/forbidden';
  return false;
};

const isRepairCodeViewRouteGuard: CanActivateFn = (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
  let authPolicies: AuthPolicies = inject(AuthPolicies);
  if (authPolicies.canViewRepairCode && environment.Features.EnableRepairCode) {
    return true;
  }
  globalThis.location.href = '/forbidden';
  return false;
};

const isRepairCodeAddRouteGuard: CanActivateFn = (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
  let authPolicies: AuthPolicies = inject(AuthPolicies);
  if (authPolicies.canAddRepairCode && environment.Features.EnableRepairCode) {
    return true;
  }
  globalThis.location.href = '/forbidden';
  return false;
};

const isRepairCodeEditRouteGuard: CanActivateFn = (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
  let authPolicies: AuthPolicies = inject(AuthPolicies);
  if (authPolicies.canEditRepairCode && environment.Features.EnableRepairCode) {
    return true;
  }
  globalThis.location.href = '/forbidden';
  return false;
};

const isRepairCodeExclusionViewRouteGuard: CanActivateFn = (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
  const authPolicies: AuthPolicies = inject(AuthPolicies);
  if (authPolicies.canViewRepairCodeExclusion && environment.Features.EnableRepairCodeExclusion) {
    return true;
  }
  globalThis.location.href = '/forbidden';
  return false;
};

const isRepairCodeExclusionAddRouteGuard: CanActivateFn = (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
  const authPolicies: AuthPolicies = inject(AuthPolicies);
  if (authPolicies.canAddRepairCodeExclusion && environment.Features.EnableRepairCodeExclusion) {
    return true;
  }
  globalThis.location.href = '/forbidden';
  return false;
};

const isRepairCodeExclusionEditRouteGuard: CanActivateFn = (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
  const authPolicies: AuthPolicies = inject(AuthPolicies);
  if (authPolicies.canEditRepairCodeExclusion && environment.Features.EnableRepairCodeExclusion) {
    return true;
  }
  globalThis.location.href = '/forbidden';
  return false;
};

const isPartExclusionViewRouteGuard: CanActivateFn = () => {
  const authPolicies = inject(AuthPolicies);
  if (authPolicies.canViewPartExclusion && environment.Features.EnablePartExclusion) {
    return true;
  }
  globalThis.location.href = '/forbidden';
  return false;
};

const isPartExclusionEditRouteGuard: CanActivateFn = () => {
  const authPolicies = inject(AuthPolicies);
  if (authPolicies.canEditPartExclusion && environment.Features.EnablePartExclusion) {
    return true;
  }
  globalThis.location.href = '/forbidden';
  return false;
};

const isPartExclusionAddRouteGuard: CanActivateFn = () => {
  const authPolicies = inject(AuthPolicies);
  if (authPolicies.canAddPartExclusion && environment.Features.EnablePartExclusion) {
    return true;
  }
  globalThis.location.href = '/forbidden';
  return false;
};

const isContainerGradeRepairCodeViewRouteGuard: CanActivateFn = (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
  const authPolicies: AuthPolicies = inject(AuthPolicies);
  if (authPolicies.canViewContainerGradeRepairCode && environment.Features.EnableContainerGradeRepairCode) {
    return true;
  }
  globalThis.location.href = '/forbidden';
  return false;
};

const isContainerGradeRepairCodeAddRouteGuard: CanActivateFn = (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
  const authPolicies: AuthPolicies = inject(AuthPolicies);
  if (authPolicies.canAddContainerGradeRepairCode && environment.Features.EnableContainerGradeRepairCode) {
    return true;
  }
  globalThis.location.href = '/forbidden';
  return false;
};

const isContainerGradeRepairCodeEditRouteGuard: CanActivateFn = (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
  const authPolicies: AuthPolicies = inject(AuthPolicies);
  if (authPolicies.canEditContainerGradeRepairCode && environment.Features.EnableContainerGradeRepairCode) {
    return true;
  }
  globalThis.location.href = '/forbidden';
  return false;
};

const isRepairLocationViewRouteGuard: CanActivateFn = () => {
  const authPolicies: AuthPolicies = inject(AuthPolicies);
  if (authPolicies.canViewRepairLocation && environment.Features.EnableRepairLocation) {
    return true;
  }
  globalThis.location.href = '/forbidden';
  return false;
};

const isRepairLocationAddRouteGuard: CanActivateFn = () => {
  const authPolicies: AuthPolicies = inject(AuthPolicies);
  if (authPolicies.canAddRepairLocation && environment.Features.EnableRepairLocation) {
    return true;
  }
  globalThis.location.href = '/forbidden';
  return false;
};

const isRepairLocationEditRouteGuard: CanActivateFn = () => {
  const authPolicies: AuthPolicies = inject(AuthPolicies);
  if (authPolicies.canEditRepairLocation && environment.Features.EnableRepairLocation) {
    return true;
  }
  globalThis.location.href = '/forbidden';
  return false;
};

const isRepairModeViewRouteGuard: CanActivateFn = () => {
  const authPolicies: AuthPolicies = inject(AuthPolicies);
  if (authPolicies.canViewRepairMode && environment.Features.EnableRepairMode) {
    return true;
  }
  globalThis.location.href = '/forbidden';
  return false;
};

const isRepairModeAddRouteGuard: CanActivateFn = () => {
  const authPolicies: AuthPolicies = inject(AuthPolicies);
  if (authPolicies.canAddRepairMode && environment.Features.EnableRepairMode) {
    return true;
  }
  globalThis.location.href = '/forbidden';
  return false;
};

const isRepairModeEditRouteGuard: CanActivateFn = () => {
  const authPolicies: AuthPolicies = inject(AuthPolicies);
  if (authPolicies.canEditRepairMode && environment.Features.EnableRepairMode) {
    return true;
  }
  globalThis.location.href = '/forbidden';
  return false;
};

const canViewUsersRouteGuard: CanActivateFn & CanActivateChildFn = (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
  let authPolicies: AuthPolicies = inject(AuthPolicies);
  if (authPolicies.canViewUsers) {
    return true;
  }
  globalThis.location.href = '/forbidden';
  return false;
};

const canAddEditUsersRouteGuard: CanActivateFn = (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
  let authPolicies: AuthPolicies = inject(AuthPolicies);
  if (authPolicies.canAddEditUsers) {
    return true;
  }
  globalThis.location.href = '/forbidden';
  return false;
};

const isScorecardLandingRouteGuard: CanActivateFn = (_route: ActivatedRouteSnapshot, _state: RouterStateSnapshot) => {
  const authPolicies = inject(AuthPolicies);
  if (authPolicies.isShopUser) {
    const shops = AuthHelper.user?.maintenanceShops;
    if (shops && shops.length > 0) {
      return inject(Router).createUrlTree(['/scorecard', shops[0].maintenanceShopCode]);
    }
  }
  return true;
};

export const APP_ROUTES: Routes = [
  {
    path: '',
    component: ShellComponent,
    canActivate: [userAuthenticatedRouteGuard],
    // Protects all nested child routes — no need to repeat userAuthenticatedRouteGuard on each child
    canActivateChild: [userAuthenticatedRouteGuard],
    children: [
      {
        path: 'workorders/edit/:workorderId',
        component: CreateWorkOrderComponent,
        canActivate: [isUserShopOrAdminRouteGuard],
      },
      {
        path: 'workorders/create/single-equipment',
        component: CreateWorkOrderComponent,
        canActivate: [isUserShopOrAdminRouteGuard],
      },
      {
        path: 'workorders',
        component: ListWorkOrdersComponent,
      },
      {
        path: 'workorders/create/multiple-equipments',
        component: BulkWorkOrderComponent,
        canActivate: [isUserShopOrAdminRouteGuard],
      },
      {
        path: 'workorders/create/multiple-equipments-vessel-shop',
        component: VesselWoComponent,
        canActivate: [isVesselWoFileProcessingRouteGuard],
      },
      {
        path: 'workorders/:id',
        component: ViewWorkOrderComponent,
      },
      {
        path: 'reports/workorders-export',
        loadComponent: () => import('./views/report/report.component').then((mod) => mod.ReportComponent),
      },
      {
        path: 'reports/operational-report',
        loadComponent: () =>
          import('./views/operational-report/operational-report.component').then((mod) => mod.OperationalReportComponent),
        canActivate: [isUserGSCOrAdminOrAppSupportAdminRouteGuard],
      },
      {
        path: 'reports/replaced-parts-export',
        loadComponent: () =>
          import('./views/replaced-parts-report/replaced-parts-report.component').then((mod) => mod.ReplacedPartsReportComponent),
      },
      {
        path: 'container-dmrl',
        component: ConatainerMrlComponent,
      },
      {
        path: 'container-visibility',
        component: InAdvanceContainerVisibilityComponent,
        canActivate: [isContainerVisibilityRouteGuard],
      },
      {
        path: 'container-stock',
        loadComponent: () => import('./views/container-stock/container-stock.component').then((m) => m.ContainerStockComponent),
        canActivate: [isContainerStockRouteGuard],
      },
      {
        path: 'booking-assignment',
        component: BookingAssignmentComponent,
        canActivate: [canViewBookingAssignmentRouteGuard],
      },
      {
        path: 'booking-assignment/:shopCode/:bookingNumber',
        loadComponent: () =>
          import('./components/booking-assignment-detail/booking-assignment-detail.component').then(
            (mod) => mod.BookingAssignmentDetailComponent,
          ),
        canActivate: [canViewBookingAssignmentRouteGuard],
      },
      {
        path: 'approvals',
        children: [
          {
            path: 'all',
            component: ListAllProcessedWorkOrdersComponent,
            canActivate: [isUserGSCOrAdminOrAppSupportAdminRouteGuard],
          },
          {
            path: 'my-workorders',
            component: ListMyProcessedWorkOrdersComponent,
            canActivate: [canViewMyWorkOrdersRouteGuard],
          },
          {
            path: 'all/:id',
            loadComponent: () =>
              import('./views/manual-approval-view-work-order/manual-approval-view-work-order.component').then(
                (mod) => mod.ManualApprovalViewWorkOrderComponent,
              ),
            canActivate: [isUserGSCOrAdminOrAppSupportAdminRouteGuard],
          },
          {
            path: 'my-workorders/:id',
            loadComponent: () =>
              import('./views/manual-approval-view-work-order/manual-approval-view-work-order.component').then(
                (mod) => mod.ManualApprovalViewWorkOrderComponent,
              ),
            canActivate: [canViewMyWorkOrdersRouteGuard],
          },
        ],
      },
      {
        path: 'approvalSettings',
        children: [
          {
            path: 'disableValidation',
            component: FlagOnOffConfigComponent,
            canActivate: [canViewApprovalSettingsRouteGuard],
          },
          {
            path: 'masterDataConfig',
            component: MasterDataConfigComponent,
            canActivate: [canViewApprovalSettingsRouteGuard],
          },
        ],
      },
      {
        path: 'scorecard/settings',
        component: VscSettingsComponent,
      },
      {
        path: 'scorecard/:vendorCode',
        component: VscDetailedViewComponent,
      },
      {
        path: 'scorecard',
        component: VscListViewComponent,
        canActivate: [isScorecardLandingRouteGuard],
      },
      {
        path: 'repair-alerts',
        component: RaAlertsComponent,
      },
      {
        path: 'master-data',
        canActivate: [isMasterDataRouteGuard],
        // Ensures master-data access is re-checked on every child navigation.
        // userAuthenticatedRouteGuard is already inherited from Shell's canActivateChild.
        canActivateChild: [isMasterDataRouteGuard],
        children: [
          {
            path: '',
            component: MasterDataComponent,
          },
          {
            path: 'shop-management',
            component: ShopManagementComponent,
            canActivate: [isShopManagementViewRouteGuard],
          },
          {
            path: 'shop-management/add-new',
            component: CreateShopsComponent,
            canActivate: [isShopManagementAddRouteGuard],
          },
          {
            path: 'shop-management/edit/:shopCode',
            component: CreateShopsComponent,
            canActivate: [isShopManagementEditRouteGuard],
          },
          {
            path: 'shop-management/:shopCode',
            loadComponent: () => import('./views/shop-detail-view/shop-detail-view.component').then((m) => m.ShopDetailViewComponent),
            canActivate: [isShopManagementViewRouteGuard],
          },
          {
            path: 'parts-management',
            component: PartsManagementComponent,
            canActivate: [isPartManagementViewRouteGuard],
          },
          {
            path: 'parts-management/add-new',
            component: CreatePartComponent,
            canActivate: [isPartManagementAddRouteGuard],
          },
          {
            path: 'parts-management/edit/:id',
            component: CreatePartComponent,
            canActivate: [isPartManagementEditRouteGuard],
          },
          {
            path: 'parts-management/:id',
            component: ViewPartsManagementComponent,
            canActivate: [isPartManagementViewRouteGuard],
          },
          // Legacy route (disabled navigation); prefer /master-data/shop-management/:shopCode
          {
            path: 'shop-contract',
            component: ShopContractComponent,
            canActivate: [isShopContractViewRouteGuard],
            canActivateChild: [isShopContractViewRouteGuard],
            children: [
              {
                path: 'material-rates',
                component: MaterialRateComponent,
              },
              {
                path: 'labour-rates',
                component: LabourRatesComponent,
              },
              {
                path: 'oem-discount',
                component: OemDiscountComponent,
                canActivate: [isOemDiscountViewRouteGuard],
              },
              {
                path: 'oem-discount/add-new',
                component: OemDiscountComponent,
                canActivate: [isOemDiscountAddRouteGuard],
              },
              {
                path: 'oem-discount/:partManufacturerCode/edit',
                component: OemDiscountComponent,
                canActivate: [isOemDiscountEditRouteGuard],
              },
              {
                path: 'oem-discount/:partManufacturerCode',
                component: OemDiscountComponent,
                canActivate: [isOemDiscountViewRouteGuard],
              },
            ],
          },
          {
            path: 'damage-code',
            component: DamageCodeComponent,
            canActivate: [isDamageCodeViewRouteGuard],
          },
          {
            path: 'damage-code/:damageCode/:damageType/edit',
            component: CreateDamageCodeComponent,
            canActivate: [isDamageCodeEditRouteGuard],
          },
          {
            path: 'damage-code/add-new',
            component: CreateDamageCodeComponent,
            canActivate: [isDamageCodeAddRouteGuard],
          },
          {
            path: 'repair-part',
            component: RepairPartComponent,
            canActivate: [isRepairPartViewRouteGuard],
          },
          {
            path: 'repair-part/add-new',
            component: CreateRepairPartComponent,
            canActivate: [isRepairPartAddRouteGuard],
          },
          {
            path: 'repair-part/:repairMode/:repairCode/:partCode/edit',
            component: CreateRepairPartComponent,
            canActivate: [isRepairPartEditRouteGuard],
          },
          {
            path: 'repair-part',
            component: RepairPartComponent,
            canActivate: [isRepairPartViewRouteGuard],
          },
          {
            path: 'repair-part/add-new',
            component: CreateRepairPartComponent,
            canActivate: [isRepairPartAddRouteGuard],
          },
          {
            path: 'repair-part/:repairMode/:repairCode/:partCode/edit',
            component: CreateRepairPartComponent,
            canActivate: [isRepairPartEditRouteGuard],
          },
          {
            path: 'repair-code',
            component: RepairCodeListComponent,
            canActivate: [isRepairCodeViewRouteGuard],
          },
          {
            path: 'repair-code/add-new',
            component: CreateRepairCodeComponent,
            canActivate: [isRepairCodeAddRouteGuard],
          },
          {
            path: 'repair-code/:repairMode/:repairCode/edit',
            component: CreateRepairCodeComponent,
            canActivate: [isRepairCodeEditRouteGuard],
          },
          {
            path: 'repair-code/:repairMode/:repairCode',
            loadComponent: () => import('./views/view-repair-code/view-repair-code.component').then((m) => m.ViewRepairCodeComponent),
            canActivate: [isRepairCodeViewRouteGuard],
          },
          {
            path: 'repair-code-exclusion',
            component: RepairCodeExclusionListComponent,
            canActivate: [isRepairCodeExclusionViewRouteGuard],
          },
          {
            path: 'repair-code-exclusion/add-new',
            component: CreateRepairCodeExclusionComponent,
            canActivate: [isRepairCodeExclusionAddRouteGuard],
          },
          {
            path: 'repair-code-exclusion/:repairMode/:repairCode/edit',
            component: CreateRepairCodeExclusionComponent,
            canActivate: [isRepairCodeExclusionEditRouteGuard],
          },
          {
            path: 'repair-code-exclusion/:repairMode/:repairCode',
            loadComponent: () =>
              import('./views/view-repair-code-exclusion/view-repair-code-exclusion.component').then(
                (m) => m.ViewRepairCodeExclusionComponent,
              ),
            canActivate: [isRepairCodeExclusionViewRouteGuard],
          },
          {
            path: 'part-exclusion',
            component: PartExclusionComponent,
            canActivate: [isPartExclusionViewRouteGuard],
          },
          {
            path: 'part-exclusion/add-new',
            component: PartExclusionFormComponent,
            canActivate: [isPartExclusionAddRouteGuard],
          },
          {
            path: 'part-exclusion/:repairMode/:partNumber/edit',
            component: PartExclusionFormComponent,
            canActivate: [isPartExclusionEditRouteGuard],
          },
          {
            path: 'part-exclusion/:repairMode/:partNumber',
            loadComponent: () =>
              import('./views/part-exclusion-detail/part-exclusion-detail.component').then((m) => m.PartExclusionDetailComponent),
            canActivate: [isPartExclusionViewRouteGuard],
          },
          {
            path: 'container-grade-repair-code',
            component: ContainerGradeRepairCodeListComponent,
            canActivate: [isContainerGradeRepairCodeViewRouteGuard],
          },
          {
            path: 'container-grade-repair-code/add-new',
            component: CreateContainerGradeRepairCodeComponent,
            canActivate: [isContainerGradeRepairCodeAddRouteGuard],
          },
          {
            path: 'container-grade-repair-code/:repairCode/:repairMode/edit',
            component: CreateContainerGradeRepairCodeComponent,
            canActivate: [isContainerGradeRepairCodeEditRouteGuard],
          },
          {
            path: 'repair-location',
            component: RepairLocationListComponent,
            canActivate: [isRepairLocationViewRouteGuard],
          },
          {
            path: 'repair-location/add-new',
            component: CreateRepairLocationComponent,
            canActivate: [isRepairLocationAddRouteGuard],
          },
          {
            path: 'repair-location/:code/edit',
            component: CreateRepairLocationComponent,
            canActivate: [isRepairLocationEditRouteGuard],
          },
          {
            path: 'repair-mode',
            component: RepairModeListComponent,
            canActivate: [isRepairModeViewRouteGuard],
          },
          {
            path: 'repair-mode/add-new',
            component: CreateRepairModeComponent,
            canActivate: [isRepairModeAddRouteGuard],
          },
          {
            path: 'repair-mode/:mode/edit',
            component: CreateRepairModeComponent,
            canActivate: [isRepairModeEditRouteGuard],
          },
        ],
      },
      {
        path: 'app-support',
        children: [
          {
            path: 'retrigger',
            component: RetriggerWorkOrderComponent,
            canActivate: [isRetriggerRouteGuard],
          },
          {
            path: 'shop',
            component: ShopComponent,
            canActivate: [isUserAppSupportAdminRouteGuard],
          },
        ],
      },
      {
        path: 'users',
        canActivate: [canViewUsersRouteGuard],
        canActivateChild: [canViewUsersRouteGuard],
        children: [
          {
            path: '',
            component: UserComponent,
          },
          {
            path: 'create',
            component: CreateUserComponent,
            canActivate: [canAddEditUsersRouteGuard],
          },
          {
            path: 'edit/:id',
            component: CreateUserComponent,
            canActivate: [canAddEditUsersRouteGuard],
          },
          {
            path: ':id',
            component: UserDetailsComponent,
          },
        ],
      },
      {
        path: 'validation-errors',
        component: ValidationErrorsComponent,
      },
      {
        path: 'assistant',
        component: ChatbotComponent,
      },
      {
        path: 'rise',
        canActivate: [isEmailIdAllowedRouteGuard],
        canActivateChild: [isEmailIdAllowedRouteGuard],
        children: [
          {
            path: 'cost-savings',
            loadComponent: () => import('./views/rise/cost-savings/cost-savings.component').then((m) => m.CostSavingsComponent),
          },
          {
            path: 'cost-savings/:shopCode',
            loadComponent: () =>
              import('./views/rise/cost-savings-detail/cost-savings-detail.component').then((m) => m.CostSavingsDetailComponent),
          },
          {
            path: 'workorders',
            loadComponent: () => import('./views/rise/workorders/workorders.component').then((m) => m.RiseWorkordersComponent),
          },
          {
            path: 'workorders/:workOrderId',
            loadComponent: () =>
              import('./views/rise/workorder-detail/workorder-detail.component').then((m) => m.RiseWorkorderDetailComponent),
          },
          {
            path: 'image-analytics',
            canActivate: [isRiseImageAnalyticsRouteGuard],
            canActivateChild: [isRiseImageAnalyticsRouteGuard],
            children: [
              { path: '', redirectTo: 'work-orders', pathMatch: 'full' },
              {
                path: 'work-orders',
                loadComponent: () =>
                  import('./views/rise/image-analytics/work-orders/image-analytics-work-orders.component').then(
                    (m) => m.ImageAnalyticsWorkOrdersComponent,
                  ),
              },
              {
                path: 'work-orders/:workOrderId',
                loadComponent: () =>
                  import('./views/rise/image-analytics/work-order-detail/image-analytics-work-order-detail.component').then(
                    (m) => m.ImageAnalyticsWorkOrderDetailComponent,
                  ),
              },
              {
                path: 'images',
                loadComponent: () =>
                  import('./views/rise/image-analytics/images/image-analytics-images.component').then(
                    (m) => m.ImageAnalyticsImagesComponent,
                  ),
              },
            ],
          },
        ],
      },
      {
        path: 'import-demand-configuration',
        loadComponent: () =>
          import('./views/import-demand-configuration/import-demand-configuration.component').then(
            (m) => m.ImportDemandConfigurationComponent,
          ),
      },
      {
        path: '',
        redirectTo: '/workorders',
        pathMatch: 'full',
      },
    ],
  },
  {
    path: 'oauth2/callback',
    component: AuthCallback,
  },
  {
    path: 'forbidden',
    component: ForbiddenComponent,
  },
];

@NgModule({
  imports: [RouterModule.forRoot(APP_ROUTES)],
  exports: [RouterModule],
})
export class AppRoutingModule {}
